import * as React from 'react';
import isEmpty from 'lodash/isEmpty';
import { AxiosResponse } from 'axios';
import { createSSRStore, toSerializedDataProps } from '@awning/archie';
import { useRouter } from 'next/router';
import withSession from '@/src/auth/withSession';
import { AuthServerSidePropsContext, TBasePageProps, TGetServerSideProps } from 'types';
import { TUser, userStoreCreator } from '@/src/shared/userStore';
import ServerAxios from '@/src/ServerAxios';
import {
  EFilterPages,
  FILTER_DEFAULTS,
  filterStoreCreator,
  toServerQueryState,
} from '@/src/Filters';
import {
  ESTIMATOR_BASE_MODEL_PARAMS,
  Estimator,
  Navigation,
  ScheduleAnalysis,
  TEstimatorStore,
  estimatorStoreCreator,
} from '@/src/estimator';
import { useEstimatorFilterStore } from '@/src/Filters';
import { SignupModal } from '@/src/SignupModal';
import { SignupBanner } from '@/src/SignupBanner/SignupBanner';
// import { maxListingCardWidth } from '@/src/estimator/EstimatorListings/Card';

type EstimatorContainerProps = TBasePageProps;

const EstimatorContainer: React.FC<React.PropsWithChildren<EstimatorContainerProps>> = () => {
  const { query, push } = useRouter();
  useEstimatorFilterStore(state => state.filterState);

  React.useEffect(() => {
    if (process.env.APP_ENV === 'production' && (!query.address || !query.lat || !query.lng)) {
      push('https://www.awning.com/airbnb-estimator');
    }
  }, [query.address, query.lat, query.lng]); // eslint-disable-line

  return (
    <>
      <Navigation render={() => <SignupBanner />} />
      <Estimator />

      <ScheduleAnalysis />

      <SignupModal />
    </>
  );
};

const getListings = async (
  latitude: number | string,
  longitude: number | string,
  filterQueryState: any,
  modelParams: TEstimatorStore['modelParams'],
  token: string
): Promise<
  AxiosResponse<{
    data: TEstimatorStore['listings'];
    blackBox: TEstimatorStore['blackBox'];
    modelParams: TEstimatorStore['modelParams'];
  }>
> => {
  const sixDecimals = (v: any) =>
    !v ? null : typeof latitude === 'string' ? parseFloat(v).toFixed(6) : v.toFixed(6);

  const headers = token ? { headers: { 'Authorization': `Bearer ${token}` } } : {};
  return ServerAxios.get(`${process.env.NEXT_PUBLIC_AWNING_API_URL}/get_estimate`, {
    ...headers,
    params: {
      latitude: sixDecimals(latitude),
      longitude: sixDecimals(longitude),
      modelParams,
      filterQueryState,
      mapBasedSearch: 1,
    },
  });
};

const getUser = async (token: string, userProfileId: string) => {
  return ServerAxios.get(`${process.env.NEXT_PUBLIC_AWNING_API_URL}/users/${userProfileId}/`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
};

export const getServerSideProps = withSession(
  async ({
    req,
    query,
  }: AuthServerSidePropsContext): TGetServerSideProps<EstimatorContainerProps> => {
    const user = req.session.user;

    let userData: TUser = {} as TUser;
    if (user !== undefined) {
      const userDataResponse = await getUser(user.token, user.id);
      userData = userDataResponse?.data;
    }
    const _userStore = createSSRStore(userStoreCreator, api => api.setState({ user: userData }));

    const {
      address,
      lat,
      lng,
      modelParams = '{}',
      filterState = '{}',
    } = query as {
      address: string;
      lat: string | number;
      lng: string | number;
      modelParams: string;
      filterState: any;
    };

    const activePage = EFilterPages.ESTIMATOR;
    const _filterStore = createSSRStore(filterStoreCreator, api => {
      let state = undefined;
      // semrushbot for some reason has appended `++++` at the end of the filterState which causes errors obviously when parsing filterState
      // in general if we can't parse it then dont just crash
      try {
        state = !isEmpty(filterState) ? JSON.parse(filterState as string) : undefined;
      } catch {}
      api.setState({ activePage: activePage, filterState: FILTER_DEFAULTS[activePage].FILTERS });
      api.getState().applyChangeSet(state);
    });

    let filterQueryState = toServerQueryState(
      _filterStore.getState()?.filterState,
      FILTER_DEFAULTS[EFilterPages.ESTIMATOR].INACTIVE_FILTERS,
      FILTER_DEFAULTS[EFilterPages.ESTIMATOR].TRANSFORMS
    );

    let listings: TEstimatorStore['listings'] = [];
    //@ts-expect-error
    let blackBox: TEstimatorStore['blackBox'] = {};
    //@ts-expect-error
    let _modelParams: TEstimatorStore['modelParams'] = {};
    if (lat && lng)
      ({
        data: listings,
        modelParams: _modelParams,
        blackBox,
      } = (await getListings(lat, lng, filterQueryState, JSON.parse(modelParams), user?.token))
        .data ?? {});

    const _estimatorStore = createSSRStore(estimatorStoreCreator, api => {
      api.setState({
        listings,
        address,
        blackBox,
        modelParams: _modelParams ? _modelParams : ESTIMATOR_BASE_MODEL_PARAMS,
        coordinates: {
          lat: typeof lat === 'string' ? parseFloat(lat) : lat,
          lng: typeof lng === 'string' ? parseFloat(lng) : lng,
        },
      });
    });

    return {
      props: {
        __storeData: toSerializedDataProps([_userStore, _estimatorStore, _filterStore]),
      },
    };
  }
);

export default EstimatorContainer;
